import React from 'react';
import { Space, Typography } from 'antd';
import { EnvironmentOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import * as Icons from '@ant-design/icons';

const { Text } = Typography;

type AntIconComponent = React.ComponentType<React.ComponentProps<typeof EnvironmentOutlined>>;

export interface LocationDisplayProps {
  locationId?: string;
  location?: {
    id: string;
    name?: string | null;
    code?: string | null;
    icon?: string | null;
    path?: string | null; // 階層パス（例: "工場A > 1階 > ボイラー室"）
  };
  showPath?: boolean;
  showIcon?: boolean;
  noLink?: boolean;
  size?: 'small' | 'default' | 'large';
  style?: React.CSSProperties;
}

/**
 * エリア情報を統一的に表示するコンポーネント
 *
 * @example
 * // 基本的な使用（リンク付き）
 * <LocationDisplay location={location} />
 *
 * @example
 * // 階層パス付き
 * <LocationDisplay location={location} showPath />
 *
 * @example
 * // リンクなし
 * <LocationDisplay location={location} noLink />
 */
export const LocationDisplay = React.forwardRef<HTMLDivElement, LocationDisplayProps>(
  (
    {
      locationId,
      location,
      showPath = false,
      showIcon = true,
      noLink = false,
      size = 'default',
      style
    }: LocationDisplayProps,
    ref
  ): JSX.Element | null => {
    const navigate = useNavigate();

    // locationオブジェクトまたはlocationIdが必要
    if (!location && !locationId) {
      return null;
    }

    const effectiveLocation = location || {
      id: locationId!,
      name: null,
      code: null,
      icon: null,
      path: null
    };

    const displayName = effectiveLocation.name || effectiveLocation.code || effectiveLocation.id;
    const displayText = showPath && effectiveLocation.path ? effectiveLocation.path : displayName;

    const handleClick = () => {
      if (!noLink && effectiveLocation.id) {
        // エリアページへ遷移し、該当エリアをハイライト
        navigate(`/app/locations?highlight=${effectiveLocation.id}`);
      }
    };

    // アイコンコンポーネントの取得
    const IconComponent = effectiveLocation.icon
      ? ((Icons as unknown as Record<string, AntIconComponent>)[effectiveLocation.icon] ||
          EnvironmentOutlined)
      : EnvironmentOutlined;

    const iconSize = size === 'small' ? 14 : size === 'large' ? 20 : 16;
    const fontSize = size === 'small' ? 12 : size === 'large' ? 16 : 14;

    return (
      <Space
        ref={ref}
        size={8}
        style={{ cursor: noLink ? 'default' : 'pointer', ...style }}
      >
        {showIcon && <IconComponent style={{ fontSize: iconSize, color: '#52c41a' }} />}
        {noLink ? (
          <Text style={{ fontSize }}>{displayText}</Text>
        ) : (
          <Text onClick={handleClick} style={{ fontSize, cursor: 'pointer' }}>
            {displayText}
          </Text>
        )}
      </Space>
    );
  });

LocationDisplay.displayName = 'LocationDisplay';
